import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, ListGroup, Row, Spinner } from "react-bootstrap";
import { approveWorkHour, getAdminWorkHours, getWorkHoursAnalysis } from "../../api/requests";

const WorkHoursAdmin = () => {
    const [mode, setMode] = useState("entries");

    const [q, setQ] = useState("");
    const [showAll, setShowAll] = useState(false);

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const currentYear = new Date().getFullYear();
    const years = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];

    const [analysisYear, setAnalysisYear] = useState(currentYear);
    const [analysisOnlyZero, setAnalysisOnlyZero] = useState(false);

    const [analysisItems, setAnalysisItems] = useState([]);
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const [analysisError, setAnalysisError] = useState(null);

    const [approvingId, setApprovingId] = useState(null);

    const load = async () => {
        setError(null);
        setLoading(true);
        try {
            const res = await getAdminWorkHours({
                q,
                approved: showAll ? undefined : false,
            });
            setItems(Array.isArray(res) ? res : []);
        } catch (e) {
            console.error(e);
            setError(e);
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    const loadAnalysis = async () => {
        setAnalysisError(null);
        setAnalysisLoading(true);
        try {
            const res = await getWorkHoursAnalysis({
                year: analysisYear,
                onlyZero: analysisOnlyZero,
            });
            setAnalysisItems(Array.isArray(res) ? res : []);
        } catch (e) {
            console.error(e);
            setAnalysisError(e);
            setAnalysisItems([]);
        } finally {
            setAnalysisLoading(false);
        }
    };

    useEffect(() => {
        if (mode !== "entries") return;
        load();
        // eslint-disable-next-line
    }, [showAll, mode]);

    useEffect(() => {
        if (mode !== "analysis") return;
        loadAnalysis();
        // eslint-disable-next-line
    }, [mode, analysisYear, analysisOnlyZero]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return String(dateStr);
        return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
    };

    const formatDateTime = (dateTimeStr) => {
        if (!dateTimeStr) return "";
        const d = new Date(dateTimeStr);
        if (Number.isNaN(d.getTime())) return String(dateTimeStr);
        return d.toLocaleString("de-DE", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatHours = (val) => {
        if (val === null || val === undefined) return "0";
        return String(val);
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        await load();
    };

    const handleApprove = async (id) => {
        setApprovingId(id);
        setError(null);
        try {
            await approveWorkHour(id);
            await load();
        } catch (e) {
            console.error(e);
            setError(e);
        } finally {
            setApprovingId(null);
        }
    };

    return (
        <Container>
            <h1>Arbeitsstunden</h1>

            <div className="mb-3" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button
                    variant={mode === "entries" ? "primary" : "outline-primary"}
                    onClick={() => setMode("entries")}
                >
                    Einträge
                </Button>
                <Button
                    variant={mode === "analysis" ? "primary" : "outline-primary"}
                    onClick={() => setMode("analysis")}
                >
                    Analyse
                </Button>
            </div>

            {mode === "entries" ? (
                <>
                    <Card className="mb-3">
                        <Card.Body>
                            <Form onSubmit={handleSearch}>
                                <Row className="g-2" style={{ alignItems: "end" }}>
                                    <Col xs={12} md={7}>
                                        <Form.Group>
                                            <Form.Label>Suche (Name oder Tätigkeit)</Form.Label>
                                            <Form.Control value={q} onChange={(e) => setQ(e.target.value)} />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={3}>
                                        <Form.Check
                                            type="switch"
                                            id="workhours-showall"
                                            label={showAll ? "Alle anzeigen" : "Nur ausstehend"}
                                            checked={showAll}
                                            onChange={(e) => setShowAll(e.target.checked)}
                                        />
                                    </Col>
                                    <Col xs={12} md={2}>
                                        <Button type="submit" disabled={loading} style={{ width: "100%" }}>
                                            {loading ? "Lade..." : "Suchen"}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>

                            {error && (
                                <Alert variant="danger" className="mt-3">
                                    Laden/Aktion fehlgeschlagen.
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>

                    {loading ? (
                        <Spinner animation="border" size="sm" />
                    ) : items.length === 0 ? (
                        <div style={{ opacity: 0.8 }}>Keine Einträge gefunden.</div>
                    ) : (
                        <ListGroup>
                            {items.map((w) => (
                                <ListGroup.Item key={w.id}>
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                                        <div>
                                            <div style={{ fontWeight: 600 }}>
                                                {w.member_display_name || "(unbekannt)"} · {w.taetigkeit}
                                            </div>
                                            <div style={{ opacity: 0.85 }}>
                                                {formatDate(w.datum)} · {w.stunden}h
                                            </div>
                                            {w.details ? (
                                                <div style={{ marginTop: 6, opacity: 0.9 }}>{w.details}</div>
                                            ) : null}
                                            {w.is_approved ? (
                                                <div style={{ marginTop: 6, opacity: 0.8 }}>
                                                    Freigegeben von {w.approved_by_username || "?"}
                                                    {w.approved_at ? ` am ${formatDateTime(w.approved_at)}` : ""}
                                                </div>
                                            ) : null}
                                        </div>

                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 8,
                                                alignItems: "flex-end",
                                            }}
                                        >
                                            {w.is_approved ? (
                                                <Badge bg="success">Freigegeben</Badge>
                                            ) : (
                                                <Badge bg="warning" text="dark">
                                                    Ausstehend
                                                </Badge>
                                            )}

                                            {!w.is_approved && (
                                                <Button
                                                    size="sm"
                                                    variant="primary"
                                                    onClick={() => handleApprove(w.id)}
                                                    disabled={approvingId === w.id}
                                                >
                                                    {approvingId === w.id ? "..." : "Freigeben"}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </>
            ) : (
                <>
                    <Card className="mb-3">
                        <Card.Body>
                            <Row className="g-2" style={{ alignItems: "end" }}>
                                <Col xs={12} md={4}>
                                    <Form.Group>
                                        <Form.Label>Jahr</Form.Label>
                                        <Form.Select
                                            value={analysisYear}
                                            onChange={(e) => setAnalysisYear(parseInt(e.target.value, 10))}
                                        >
                                            {years.map((y) => (
                                                <option key={y} value={y}>
                                                    {y}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={4}>
                                    <Form.Check
                                        type="switch"
                                        id="workhours-onlyzero"
                                        label="Nur ohne Einträge"
                                        checked={analysisOnlyZero}
                                        onChange={(e) => setAnalysisOnlyZero(e.target.checked)}
                                    />
                                </Col>
                                <Col xs={12} md={4}>
                                    <Button
                                        variant="secondary"
                                        onClick={loadAnalysis}
                                        disabled={analysisLoading}
                                        style={{ width: "100%" }}
                                    >
                                        {analysisLoading ? "Lade..." : "Aktualisieren"}
                                    </Button>
                                </Col>
                            </Row>

                            <div style={{ marginTop: 8, opacity: 0.8 }}>Summen zählen nur freigegebene Stunden.</div>

                            {analysisError && (
                                <Alert variant="danger" className="mt-3">
                                    Laden fehlgeschlagen.
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>

                    {analysisLoading ? (
                        <Spinner animation="border" size="sm" />
                    ) : analysisItems.length === 0 ? (
                        <div style={{ opacity: 0.8 }}>Keine Daten gefunden.</div>
                    ) : (
                        <ListGroup>
                            {analysisItems.map((a) => (
                                <ListGroup.Item key={a.member_id}>
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                                        <div>
                                            <div style={{ fontWeight: 600 }}>
                                                {a.display_name || a.username || "(unbekannt)"}
                                            </div>
                                            {a.username ? <div style={{ opacity: 0.75 }}>@{a.username}</div> : null}
                                        </div>

                                        <div style={{ textAlign: "right" }}>
                                            <div style={{ fontWeight: 600 }}>{formatHours(a.approved_hours_year)}h</div>
                                            <div style={{ opacity: 0.75 }}>{a.entries_year} Einträge</div>
                                        </div>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </>
            )}
        </Container>
    );
};

export default WorkHoursAdmin;
